import { createSlice } from '@reduxjs/toolkit';
import { env } from 'config/env';
import { BREAKPOINTS } from 'config/breakpoints';
import { isServer } from 'library/helpers/isServer';
import { getDefaultPath } from 'library/helpers/url_sync';
import { getView } from 'library/helpers/utility';

const preKeys = getDefaultPath();
const savedEnvironment = !isServer && localStorage.getItem('selectedEnvironment');
const initialState = {
	collapsed: !isServer && window.innerWidth < BREAKPOINTS.xl,
	view: !isServer && getView(window.innerWidth),
	width: !isServer && window.innerWidth,
	height: !isServer && window.innerHeight,
	openKeys: preKeys,
	current: preKeys,
	dashboard: {
		selected: localStorage.getItem('d') || 'access', // default, access, locker, Rooms, profile,
		switching: false
	},
	userLoadingComplete: false,
	// Site scope (Portfolio-Aware Access Dashboard)
	// selectedSiteID: 'all' = All Sites / portfolio mode; number = specific site
	// siteScopeHydrated: false until useScopeUrlSync reconciles the URL with loaded sites
	selectedSiteID: 'all',
	siteScopeHydrated: false,
	// Last 'orgID:siteID' scope the coordinator hydrated — onScopeChange skips
	// repeat setSelectedSite dispatches for the same scope (boot hydration
	// fires several). Lives in the store so logout resets it with everything
	// else and it's visible in devtools.
	hydratedScopeKey: null,
	// Global drawers — stack supports nesting (each entry is a drawer layer)
	drawers: [],
	_drawerSeq: 0,

	sync: {
		step: '',
		bulkCredentialProgress: null
	},
	// No trailing `|| 'production'`. It was load-bearing before — the old expression read
	// REACT_APP_ENV raw, so an absent variable fell out of the ternary as `undefined` and the tail
	// supplied the default. `env.appEnv` now substitutes 'production' itself, so both arms are
	// non-empty and the tail is dead HERE. 🛑 That means the default lives in `env.appEnv` alone:
	// drop it there and this line silently yields `undefined`, not 'production'.
	environment: savedEnvironment || (env.isStaging ? 'development' : env.appEnv)
};

const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		collapseChange(state) {
			state.collapsed = !state.collapsed;
		},
		sideBarChange(state) {
			state.showSidebar = !state.showSidebar;
		},
		toggleAll(state, action) {
			const appView = getView(action.payload.width);
			if (
				state.view !== appView ||
				action.payload.height !== state.height ||
				action.payload.width !== state.width
			) {
				const height = action.payload.height ? action.payload.height : state.height;
				const width = action.payload.width ? action.payload.width : state.width;
				state.collapsed = window.innerWidth < BREAKPOINTS.xl;
				state.view = appView;
				state.width = width;
				state.height = height;
			}
		},
		changeOpenKeys(state, action) {
			state.openKeys = action.payload;
		},
		changeCurrent(state, action) {
			state.current = action.payload;
		},
		// Actions for changing the dashboard
		switchDashboard: (state, action) => {
			localStorage.setItem('d', action.payload);
			state.dashboard.selected = action.payload;
			state.dashboard.switching = true;
			state.drawers = []; // Close drawers from the previous dashboard context
		},
		switchDashboardSuccess(state) {
			state.dashboard.switching = false;
		},
		switchDashboardError(state, action) {
			state.dashboard.switching = false;
			state.error = action.payload;
		},
		setUserLoadingComplete(state) {
			state.userLoadingComplete = true;
		},
		loadAccessDashboardData: (state) => {},
		loadLockersDashboardData: (state) => {},
		clearMenu(state) {
			state.openKeys = [];
			state.current = [];
		},
		setEnvironment(state, action) {
			state.environment = action.payload;
			localStorage.setItem('selectedEnvironment', action.payload);
		},
		setSyncStep(state, action) {
			state.sync.step = action.payload;
		},
		clearSyncStep(state) {
			state.sync.step = '';
		},
		setBulkCredentialProgress(state, action) {
			state.sync.bulkCredentialProgress = action.payload;
		},
		clearBulkCredentialProgress(state) {
			state.sync.bulkCredentialProgress = null;
		},
		// Site scope reducers
		setSelectedSite(state, action) {
			state.selectedSiteID = action.payload ?? 'all';
			state.siteScopeHydrated = true;
		},
		setSiteScopeHydrated(state, action) {
			state.siteScopeHydrated = action.payload;
		},
		setHydratedScopeKey(state, action) {
			state.hydratedScopeKey = action.payload;
		},

		// ── Global drawers ─────────────────────────────────────────
		openDrawer(state, action) {
			const { type, id, activeTab, focusSection, highlightSource, navList, entityLabel } = action.payload;
			// An id-less entry mounts a skeleton with no way to close it. Guarded here, not in useDrawer,
			// because updateDrawerEntryId is a second write path.
			if (id == null) return;
			state._drawerSeq += 1;
			state.drawers.push({
				_key: state._drawerSeq,
				type,
				id,
				activeTab,
				focusSection,
				highlightSource,
				navList,
				entityLabel
			});
		},
		updateDrawerEntryId(state, action) {
			const { _key, id } = action.payload;
			if (id == null) return;
			const entry = state.drawers.find((d) => d._key === _key);
			if (entry) entry.id = id;
		},
		closeDrawer(state, action) {
			const key = action.payload;
			if (key != null) {
				// Close this drawer + everything above it (mimics nested unmount)
				const idx = state.drawers.findIndex((d) => d._key === key);
				if (idx >= 0) state.drawers = state.drawers.slice(0, idx);
			} else {
				state.drawers.pop();
			}
		},
		closeAllDrawers(state) {
			state.drawers = [];
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => {
			return initialState; // Reset state on logout
		});
		// Org switch: drop scope back to pending; useScopeUrlSync re-hydrates against the new org
		builder.addCase('auth/switchProfileSuccess', (state) => {
			state.selectedSiteID = 'all';
			state.siteScopeHydrated = false;
			state.hydratedScopeKey = null; // Force onScopeChange to reload all data for the new org
			state.drawers = []; // Close drawers from the previous org context
		});
	}
});

export const appActions = appSlice.actions;

export default appSlice.reducer;
